import { FC, useCallback, useEffect, useState } from "react";
import AppLayout from "../../components/layout/app-layout/app-layout";
import DataTable from "../../components/shared/DataTable/DataTable";
import Tabs from "../../components/shared/Tabs";
import BtButton from "../bt-button/bt-button";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { serviceSalesOrgApplication } from "../../services/salesOrgApplicationService";
import { formatDateTime } from "../../util";
import File from "../../components/File/File";
import { FileScope } from "../../util/appEnum";
import { NotificationManager } from "react-notifications";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

type Props = {
} & RouteComponentProps;

type Application = {
  _id: string,
  organizationNumber: string,
  createdAt: Date,
  contract: {
    signedAt?: Date,
    fileName?: string,
    file?: string,
    signedFileName?: string,
  },
}

const PageOrganizationApplications : FC<Props> = ({ history, location }) => {
  const [t] = useTranslation('organizations', { keyPrefix: 'applications' });
  const [loader, _setLoader] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [applications, setApplications] = useState<Application[]>([]);

  const onChangeTab = useCallback((tab: number) => {
    const base = location.pathname.split("/").slice(0, 2).join("/");
    if (tab == 0) history.push(base + "/organizations");
    if (tab == 1) history.push(base + "/organizations/inactive");
    if (tab == 2) history.push(base + "/organizations/applications");
  }, []);

  useEffect(() => {
    (async () => {
      const response = await serviceSalesOrgApplication.listPendingSignedApplications();
      setApplications(response);
    })();
  }, []);

  const approveApplication = useCallback(async (id: string) => {
    setProcessing(true);
    try {
      await serviceSalesOrgApplication.approveApplication(id);
      const response = await serviceSalesOrgApplication.listPendingSignedApplications();
      setApplications(response);
    } catch (e) {
      let err = "";
      if (e instanceof AxiosError) {
        err = e?.response?.data?.error;
      }
      NotificationManager.error([(e as Error).toString(), err].join(": "), t('notification.error', 'Misslyckat'));
    }
    setProcessing(false);
  }, []);

  const rejectApplication = useCallback(async (id: string) => {
    setProcessing(true);
    await serviceSalesOrgApplication.rejectApplication(id);
    const response = await serviceSalesOrgApplication.listPendingSignedApplications();
    setApplications(response);
    setProcessing(false);
  }, []);

  const getColumns = useCallback(() => [
    { title: t('table.header.organizationNumber', 'Organisationsnummer'), field: "organizationNumber", },
    { title: t('table.header.createdAt', 'Skapad'), field: "createdAt", render: (rowData: Application) => formatDateTime(rowData.createdAt)},
    { title: t('table.headser.signedAt', 'Signerad'), field: "contract.signedAt", render: (rowData: Application) => formatDateTime(rowData.contract.signedAt)},
    { title: t('table.header.contract', 'Kontrakt'), render: (rowData: Application) => <>
      <File
        file={rowData.contract.signedFileName}
        fileName={t('contract.fileName', 'Användaravtal {{orgNo}}.pdf', { orgNo: rowData.organizationNumber })}
        scope={FileScope.SalesOrgApplicationContract}
        scopeOptions={{ organizationNumber: rowData.organizationNumber }}
      />
    </> },
    { title: t('table.header.action', 'Åtgärd'), render: (rowData: Application) => <>
      <BtButton size="xxs" color="gray" loaderShow={processing} onClick={() => approveApplication(rowData._id)}>{t('button.approve', 'Godkänn')}</BtButton>
      {" "}
      <BtButton size="xxs" color="gray" loaderShow={processing} onClick={() => rejectApplication(rowData._id)}>{t('button.reject', 'Neka')}</BtButton>
    </> },
  ], [processing, t]);

  return (
    <AppLayout title={t('pageTitle', 'Organisationer')}>
      <div style={{ padding: "32px" }}>
        <div className="tab-container">
          <Tabs
            tabs={[
              t('tabs.active', 'Aktiva organisationer'),
              t('tabs.inactive', 'Inaktiva organisationer'),
              t('tabs.applications', 'Ansökningar ({{count}})', { count: applications.length })
            ]}
            selected={2}
            onChange={onChangeTab}
          />
          <div className="tab-actions">
            <BtButton onClick={() => {}}>
              {t('button.add', 'Lägg till organisation')}
            </BtButton>
          </div>
        </div>

        <DataTable
          isLoading={loader}
          columns={getColumns()}
          data={applications}
        />

      </div>
    </AppLayout>
  );
}

export default withRouter(PageOrganizationApplications);
